import { cloneElement, CSSProperties, FC, useState } from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

import { Position } from "./Menu";

type ContainerProps = {
  $top?: boolean;
  $position?: Position;
  $isChild?: boolean;
};

const Container = styled.div<ContainerProps>`
  position: absolute;
  background-color: var(--color-neutral-000-full);
  border-radius: var(--size-border-radius-small);
  box-shadow: var(--elevation-4);
  z-index: 1;
  color: var(--color-neutral-800);
  ${({ $top, $isChild }) => {
    return css`
      ${$top ? "bottom" : "top"}: ${$isChild ? "0%" : "100%"};
    `;
  }}
  ${({ $isChild, $position }): FlattenSimpleInterpolation => {
    switch ($position) {
      case "right":
        return css`
          left: ${$isChild ? "100%" : "0"};
        `;
      case "left":
        return css`
          right: ${$isChild ? "100%" : "0"};
        `;
      default:
        return css``;
    }
  }}
`;

export type OptionProps = {
  content: JSX.Element;
  maxHeight?: number;
  options?: OptionProps[];
  disabled?: boolean;
};

type MenuOptionsProps = {
  top: boolean;
  position: Position;
  options: OptionProps[];
  style?: CSSProperties;
  parentKey?: string;
  isChild?: boolean;
};

export const overflowStyle = (maxHeight?: number): CSSProperties => {
  if (maxHeight !== undefined) {
    return {
      maxHeight: `${maxHeight}px`,
      overflowY: "auto",
    };
  }
  return {};
};

export const MenuOptions: FC<MenuOptionsProps> = ({
  top,
  position,
  options = [],
  style,
  parentKey = "option",
  isChild,
}) => {
  const [optionSelected, setOptionSelected] = useState(-1);

  const selectOption = (index: number): void => {
    setOptionSelected(index);
  };

  return (
    <Container $top={top} $position={position} $isChild={isChild} style={style}>
      {options.map((option, index) => {
        const { content, disabled, options: op, maxHeight } = option;
        const key = `${parentKey}-${index}`;
        return (
          <div
            key={key}
            onMouseEnter={(): void => selectOption(disabled ? -1 : index)}
            onMouseLeave={(): void => selectOption(-1)}
          >
            {cloneElement(content, {
              isMenu: Boolean(op),
              isFirst: index === (options ? 0 : -1),
              isLast: index === (options ? options.length - 1 : -1),
              disabled,
              children: [
                content.props.children,
                op && optionSelected === index && (
                  <MenuOptions
                    options={op}
                    position={position}
                    top={top}
                    isChild
                    style={overflowStyle(maxHeight)}
                    parentKey={key}
                  />
                ),
              ],
            })}
          </div>
        );
      })}
    </Container>
  );
};
